import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';

const TermsOfService = () => {
  const theme = useTheme();

  const sections = [
    {
      title: 'Acceptance of Terms',
      content: [
        'By accessing and using Future Forwards Education services, you accept and agree to be bound by the terms and provision of this agreement.',
        'If you do not agree to abide by the above, please do not use this service.',
        'These terms apply to all visitors, users, and others who access or use our services.',
        'We reserve the right to modify these terms at any time without prior notice.',
      ]
    },
    {
      title: 'Description of Service',
      content: [
        'Future Forwards Education provides study abroad counseling, visa processing, university application assistance, and related educational services.',
        'We help students identify suitable educational opportunities, prepare applications, and navigate the study abroad process.',
        'Our services include but are not limited to: academic counseling, visa guidance, scholarship assistance, and pre-departure support.',
        'We work with various educational institutions and government agencies to facilitate your educational journey.',
      ]
    },
    {
      title: 'User Responsibilities',
      content: [
        'You are responsible for providing accurate and complete information during the application process.',
        'You must maintain the confidentiality of any account credentials provided to you.',
        'You agree to use our services only for lawful purposes and in accordance with these terms.',
        'You are responsible for ensuring that all information you provide is truthful and up-to-date.',
        'You must comply with all applicable laws and regulations in your home country and destination country.',
      ]
    },
    {
      title: 'Service Fees and Payment',
      content: [
        'Our services may be subject to fees as outlined in our service agreements.',
        'Payment terms and conditions will be specified in individual service contracts.',
        'All fees are non-refundable unless otherwise specified in writing.',
        'We reserve the right to change our fee structure with 30 days notice.',
        'Late payment may result in suspension or termination of services.',
      ]
    },
    {
      title: 'Limitation of Liability',
      content: [
        'Future Forwards Education acts as an intermediary and counselor. We do not guarantee admission to any educational institution.',
        'We are not responsible for decisions made by universities, colleges, or government agencies.',
        'Our liability is limited to the fees paid for our services.',
        'We are not liable for any indirect, incidental, or consequential damages.',
        'Students are responsible for meeting all admission requirements and visa conditions.',
      ]
    },
    {
      title: 'Intellectual Property',
      content: [
        'All content on our website, including text, graphics, logos, and software, is the property of Future Forwards Education.',
        'You may not reproduce, distribute, or modify our content without written permission.',
        'Our trademarks and service marks are protected by applicable intellectual property laws.',
        'You may use our materials only for personal, non-commercial purposes.',
      ]
    },
    {
      title: 'Privacy and Data Protection',
      content: [
        'We are committed to protecting your privacy and personal information.',
        'Our collection and use of personal information is governed by our Privacy Policy.',
        'We implement appropriate security measures to protect your data.',
        'You consent to our collection and use of information as described in our Privacy Policy.',
      ]
    },
    {
      title: 'Termination',
      content: [
        'Either party may terminate the service agreement with written notice.',
        'We reserve the right to terminate services for violation of these terms.',
        'Upon termination, you remain responsible for any outstanding fees.',
        'Termination does not affect any rights or obligations that have already accrued.',
      ]
    },
    {
      title: 'Dispute Resolution',
      content: [
        'Any disputes arising from these terms will be resolved through binding arbitration.',
        'Arbitration will be conducted in accordance with the laws of India.',
        'The arbitration will take place in Trivandrum, Kerala, India.',
        'Each party will bear their own legal costs unless otherwise determined by the arbitrator.',
      ]
    },
    {
      title: 'Governing Law',
      content: [
        'These terms are governed by the laws of India.',
        'Any legal action must be brought in the courts of Trivandrum, Kerala, India.',
        'If any provision of these terms is found to be unenforceable, the remaining provisions will remain in effect.',
        'These terms constitute the entire agreement between you and Future Forwards Education.',
      ]
    },
    {
      title: 'Contact Information',
      content: [
        'For questions about these Terms of Service, please contact us:',
        'Email: legal@futureforwards.in',
        'Phone: +91 7356475308, +91 7736715551',
        'Address: Trivandrum, Kerala, India',
        'We will respond to your inquiries within 30 days of receipt.',
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Header />
      
      <Box sx={{ pt: 8, pb: 6 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  color: '#1e3a5f',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#64748b',
                  maxWidth: '800px',
                  mx: 'auto',
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Please read these terms carefully before using our services. By using our services, you agree to be bound by these terms.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#64748b',
                  mt: 2,
                  fontStyle: 'italic',
                }}
              >
                Last updated: December 2024
              </Typography>
            </Box>
          </motion.div>

          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 6 },
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
            }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#1e3a5f',
                      mb: 2,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      fontWeight: 700,
                    }}
                  >
                    {section.title}
                  </Typography>
                  {section.content.map((item, itemIndex) => (
                    <Typography
                      key={itemIndex}
                      variant="body1"
                      sx={{
                        color: '#64748b',
                        mb: 2,
                        lineHeight: 1.8,
                        fontSize: '1.1rem',
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </motion.div>
            ))}
          </Paper>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default TermsOfService;
